import { mergeAttributes, Node } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { Suggestion, type SuggestionOptions } from "@tiptap/suggestion";

export type MergeFieldOption = {
    id: string;
    label: string;
    description?: string;
    /** Whether the field renders a layout block, such as a button, rather than text. */
    block?: boolean;
};

export const mergeFieldSuggestionPluginKey = new PluginKey("mergeFieldSuggestion");

export type MergeFieldOptions = {
    suggestion: Omit<SuggestionOptions<MergeFieldOption, MergeFieldOption>, "editor">;
};

/**
 * Defines an atomic inline merge-field node with HTML attributes and double-brace suggestions.
 * Postconditions: HTML stores ID/label data and displays the truthy label or ID. Commands delegate
 * to the editor; this extension does not resolve merge values or validate IDs.
 */
export const MergeField = Node.create<MergeFieldOptions>({
    name: "mergeField",
    group: "inline",
    inline: true,
    selectable: true,
    atom: true,

    /**
     * Supplies suggestion defaults that replace the query range with a merge field and trailing space.
     * Postconditions: The command focuses the editor and returns the command chain result.
     */
    addOptions() {
        return {
            suggestion: {
                char: "{{",
                pluginKey: mergeFieldSuggestionPluginKey,
                allowedPrefixes: null,
                command: ({ editor, range, props }) =>
                    editor
                        .chain()
                        .focus()
                        .insertContentAt(
                            range,
                            [
                                {
                                    type: this.name,
                                    attrs: props
                                },
                                {
                                    type: "text",
                                    text: " "
                                }
                            ]
                        )
                        .run()
            }
        };
    },

    /**
     * Defines ID/label HTML mappings; absent labels fall back to source text when parsing.
     */
    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute("data-merge-field"),
                renderHTML: ({ id }) => ({ "data-merge-field": id })
            },
            label: {
                default: null,
                parseHTML: element =>
                    element.getAttribute("data-merge-field-label") ?? element.textContent,
                renderHTML: ({ label }) => ({ "data-merge-field-label": label })
            }
        };
    },

    /**
     * Recognizes merge fields only from spans carrying the merge-field data attribute.
     */
    parseHTML() {
        return [{ tag: "span[data-merge-field]" }];
    },

    /**
     * Serializes the node as a styled span retaining supplied HTML attributes.
     */
    renderHTML({ node, HTMLAttributes }) {
        return [
            "span",
            mergeAttributes(HTMLAttributes, { class: "tmd-merge-field" }),
            node.attrs.label || node.attrs.id
        ];
    },

    /**
     * Adds direct merge-field insertion without the suggestion command's trailing space.
     */
    addCommands() {
        return {
            insertMergeField:
                (attributes: MergeFieldOption) =>
                    ({ commands }) =>
                        commands.insertContent({
                            type: this.name,
                            attrs: attributes
                        })
        };
    },

    /**
     * Creates the suggestion plugin with configured options overriding the default editor property.
     */
    addProseMirrorPlugins() {
        return [
            Suggestion<MergeFieldOption, MergeFieldOption>({
                editor: this.editor,
                ...this.options.suggestion
            })
        ];
    }
});

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        mergeField: {
            insertMergeField: (attributes: MergeFieldOption) => ReturnType;
        };
    }
}
